import { useLingui } from "@lingui/react/macro";
import { useFormContext, type FieldValues } from "react-hook-form";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import {
  medicalCompetenceDictionary,
  medicalCompetenceSchema,
} from "@medoma/models/shifts";
import { useEnabledCompetences } from "@/api/Shifts";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { useIsInFinalValidationPhase } from "@/pages/template-manager/TemplateFieldValidationContext";
import type { TemplateCompetencePickerFields } from "@/pages/template-manager/TemplateForm/templateFormSchema";

export const TemplateCompetencePicker = ({
  isRequiredBySelectedCategory,
}: {
  isRequiredBySelectedCategory?: boolean;
}) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<FieldValues>();
  const { t } = useLingui();
  const {
    data: enabledCompetences,
    isPending,
    isError,
  } = useEnabledCompetences();

  const fieldName = useFieldPathWithPrefix<TemplateCompetencePickerFields>(
    "requiredCompetences",
  );
  const errorMessage = useFieldErrorWithPrefix<TemplateCompetencePickerFields>(
    errors,
    "requiredCompetences",
  );
  const isInFinalValidationPhase = useIsInFinalValidationPhase();
  const hasValidationRuleRequired =
    isRequiredBySelectedCategory && isInFinalValidationPhase;

  return (
    <Checkboxes
      legend={
        isRequiredBySelectedCategory
          ? t`Kompetensbehov`
          : t`Kompetensbehov (valfri)`
      }
      errorMessage={errorMessage}
      orientation="horizontal"
    >
      {isPending ? (
        <Loading message={t`Hämtar lista med kompetenser`} />
      ) : isError ? (
        <ErrorMessage message={t`Kunde inte hämta lista med kompetenser`} />
      ) : (
        enabledCompetences.map((competence) => (
          <Checkbox
            key={competence}
            label={{ text: t(medicalCompetenceDictionary[competence].long) }}
            {...register(fieldName, {
              required: hasValidationRuleRequired
                ? {
                    value: true,
                    message: t`Ange minst en kompetens`,
                  }
                : undefined,
            })}
            value={medicalCompetenceSchema.enum[competence]}
            visualStyle="framed"
          />
        ))
      )}
    </Checkboxes>
  );
};
